/**
 * Currency utilities for consistent handling of monetary values
 * 
 * SYSTEM STANDARD:
 * - Database: Store amounts in CENTS (integers)
 * - API: Send/receive amounts in CENTS (integers)
 * - Frontend: Display amounts in DOLLARS (formatted)
 * - Stripe: Send amounts in CENTS (as required by Stripe)
 */

/**
 * Convert dollars to cents for API requests
 * @param dollars - Amount in dollars (e.g., 50.25)
 * @returns Amount in cents (e.g., 5025)
 */
export const dollarsToCents = (dollars: number): number => {
  return Math.round(dollars * 100);
};

/**
 * Convert cents to dollars for display
 * @param cents - Amount in cents (e.g., 5025)
 * @returns Amount in dollars (e.g., 50.25)
 */
export const centsToDollars = (cents: number): number => {
  return cents / 100;
};

/**
 * Format cents as currency string for display
 * @param cents - Amount in cents
 * @param currency - Currency code (default: 'USD')
 * @param showCurrency - Whether to show currency symbol (default: true)
 * @returns Formatted currency string (e.g., "$50.25")
 */
export const formatCentsAsCurrency = (
  cents: number,
  currency: string = 'USD',
  showCurrency: boolean = true
): string => {
  const dollars = centsToDollars(cents);
  
  if (showCurrency) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(dollars);
  } else {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(dollars);
  }
};

/**
 * Format cents as currency string without decimals for whole amounts
 * @param cents - Amount in cents
 * @param currency - Currency code (default: 'USD')
 * @param showCurrency - Whether to show currency symbol (default: true)
 * @returns Formatted currency string without decimals (e.g., "$50")
 */
export const formatCentsAsCurrencyWhole = (
  cents: number,
  currency: string = 'USD',
  showCurrency: boolean = true
): string => {
  const dollars = centsToDollars(cents);
  
  if (showCurrency) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(dollars);
  } else {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(dollars);
  }
};

/**
 * Format dollars as currency string (for backward compatibility)
 * @param dollars - Amount in dollars
 * @param currency - Currency code (default: 'USD')
 * @param showCurrency - Whether to show currency symbol (default: true)
 * @returns Formatted currency string
 * @deprecated Use formatCentsAsCurrency instead for consistency
 */
export const formatCurrency = (
  dollars: number,
  currency: string = 'USD',
  showCurrency: boolean = true
): string => {
  if (showCurrency) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(dollars);
  } else {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(dollars);
  }
};

/**
 * Format dollars as currency string without decimals (for backward compatibility)
 * @param dollars - Amount in dollars
 * @param currency - Currency code (default: 'USD')
 * @param showCurrency - Whether to show currency symbol (default: true)
 * @returns Formatted currency string without decimals
 * @deprecated Use formatCentsAsCurrencyWhole instead for consistency
 */
export const formatCurrencyWhole = (
  dollars: number,
  currency: string = 'USD',
  showCurrency: boolean = true
): string => {
  if (showCurrency) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(dollars);
  } else {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(dollars);
  }
};

/**
 * Format a number with commas but no currency symbol (for input placeholders, etc.)
 * @param amount - The amount to format
 * @returns Formatted number string with commas
 */
export const formatNumber = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

/**
 * Validate that an amount in cents is valid
 * @param cents - Amount in cents to validate
 * @returns True if valid, false otherwise
 */
export const isValidCentsAmount = (cents: number): boolean => {
  return Number.isInteger(cents) && cents >= 0 && cents <= 99999999; // Max $999,999.99
};

/**
 * Validate that an amount in dollars is valid for conversion to cents
 * @param dollars - Amount in dollars to validate
 * @returns True if valid, false otherwise
 */
export const isValidDollarsAmount = (dollars: number): boolean => {
  return dollars >= 0 && dollars <= 999999.99;
};

/**
 * Calculate percentage of an amount in cents
 * @param cents - Base amount in cents
 * @param percentage - Percentage as decimal (e.g., 0.05 for 5%)
 * @returns Percentage amount in cents, rounded
 */
export const calculatePercentageOfCents = (cents: number, percentage: number): number => {
  return Math.round(cents * percentage);
};

// Type definitions for better type safety
export type CentsAmount = number; // Amount in cents (integer)
export type DollarsAmount = number; // Amount in dollars (decimal)

/**
 * Type guard to ensure amount is in cents
 */
export const isCentsAmount = (amount: any): amount is CentsAmount => {
  return typeof amount === 'number' && Number.isInteger(amount) && amount >= 0;
};

/**
 * Type guard to ensure amount is in dollars
 */
export const isDollarsAmount = (amount: any): amount is DollarsAmount => {
  return typeof amount === 'number' && amount >= 0;
};
